import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle } from 'react-feather';
import { Alert, Badge, Button, Card, Col, Row, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  endSupportSessionAPI,
  getSecurityCenterAPI,
  rotateAllDueSecretsAPI,
  rotateSecretAPI,
} from '../../../Services/mockApi';
import PageHeader from '../../../Components/shared/PageHeader';
import LoadingSkeleton from '../../../Components/shared/LoadingSkeleton';
import EmptyState from '../../../Components/shared/EmptyState';
import ConfirmModal from '../../../Components/shared/ConfirmModal';
import { platformUrl } from '../../../utils/mhNav';
import styles from './SecurityCenter.module.scss';

export default function SecurityCenter() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rotateAllOpen, setRotateAllOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await getSecurityCenterAPI();
      setData(res.data);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const secretRowClass = (s) => {
    if (s.status === 'overdue' || s.ageDays > 180) return styles.rowOverdue;
    if (s.status === 'due_soon' || s.ageDays > 90) return styles.rowDueSoon;
    return '';
  };

  const events = useMemo(() => (data?.securityEvents || []).slice(0, 20), [data]);

  if (loading && !data) {
    return (
      <div>
        <PageHeader title="Security center" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Audit', path: platformUrl('audit') }, { label: 'Security' }]} />
        <LoadingSkeleton rows={5} cols={2} />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <PageHeader title="Security center" breadcrumbs={[{ label: 'Platform', path: platformUrl('dashboard') }, { label: 'Audit', path: platformUrl('audit') }]} />
        <EmptyState icon={AlertTriangle} title="Could not load" description={error.message} actionLabel="Retry" onAction={load} />
      </div>
    );
  }

  const critical = (data?.criticalStaleSecrets || 0) > 0;

  return (
    <div>
      <PageHeader
        title="Security center"
        breadcrumbs={[
          { label: 'Platform', path: platformUrl('dashboard') },
          { label: 'Audit', path: platformUrl('audit') },
          { label: 'Security' },
        ]}
        secondaryActions={[{ label: 'Audit explorer', variant: 'outline-secondary', onClick: () => navigate(platformUrl('audit')) }]}
      />

      {critical && (
        <Alert variant="danger" className={`${styles.criticalBanner} d-flex flex-wrap justify-content-between align-items-center`}>
          <span>
            {data.criticalStaleSecrets} stale API secrets require rotation — rotate now
          </span>
          <Button size="sm" variant="light" onClick={() => setRotateAllOpen(true)}>
            Rotate now
          </Button>
        </Alert>
      )}

      <Row className="g-3 mb-4">
        <Col md={6} xl={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="text-muted small">Stale secrets (&gt;180d)</div>
              <div className="fs-4 fw-bold">{data.staleSecretsCount}</div>
              <Button size="sm" className="mt-2" onClick={() => setRotateAllOpen(true)}>
                Rotate all due
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} xl={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="text-muted small">Risky sign-ins</div>
              <div className="fs-4 fw-bold">{data.riskySignIns}</div>
              <Button size="sm" variant="link" className="p-0 mt-2" onClick={() => navigate(platformUrl('audit'))}>
                Review
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} xl={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="text-muted small">Active support sessions</div>
              <div className="fs-4 fw-bold">{data.activeSupportSessions}</div>
              <Button
                size="sm"
                variant="outline-danger"
                className="mt-2"
                onClick={async () => {
                  try {
                    await endSupportSessionAPI();
                    toast.success('Sessions ended (simulated)');
                    load();
                  } catch (e) {
                    toast.error(e.message);
                  }
                }}
              >
                End all
              </Button>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6} xl={3}>
          <Card className="h-100">
            <Card.Body>
              <div className="text-muted small">Failed MFA (24h)</div>
              <div className="fs-4 fw-bold">{data.failedMfa24h}</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Header>Secret rotation status</Card.Header>
        <Table responsive hover className="mb-0">
          <thead className="table-light">
            <tr>
              <th>Secret</th>
              <th>Age (days)</th>
              <th>Last rotated</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(data.secrets || []).map((s) => (
              <tr key={s.id} className={secretRowClass(s)}>
                <td>{s.name}</td>
                <td>{s.ageDays}</td>
                <td>{new Date(s.lastRotatedAt).toLocaleDateString()}</td>
                <td>
                  <Badge bg={s.status === 'overdue' ? 'danger' : s.status === 'due_soon' ? 'warning' : 'success'}>{s.status}</Badge>
                </td>
                <td>
                  <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={async () => {
                      try {
                        await rotateSecretAPI(s.id);
                        toast.success('Rotated');
                        load();
                      } catch (e) {
                        toast.error(e.message);
                      }
                    }}
                  >
                    Rotate
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>

      <Row className="g-3 mb-4">
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header>Recent security events</Card.Header>
            <Card.Body>
              <ul className={styles.timeline}>
                {events.map((e) => (
                  <li key={e.id} className={styles.timelineItem}>
                    <div className="small text-muted">{new Date(e.ts).toLocaleString()}</div>
                    <div className="fw-semibold">{e.type}</div>
                    <div>{e.actor}</div>
                    <Badge bg={e.result === 'success' ? 'success' : 'danger'}>{e.result}</Badge>{' '}
                    <Button as={Link} size="sm" variant="link" className="p-0" to={platformUrl('audit')}>
                      Open detail
                    </Button>
                  </li>
                ))}
              </ul>
            </Card.Body>
          </Card>
        </Col>
        <Col lg={6}>
          <Card className="h-100">
            <Card.Header>Recommended actions</Card.Header>
            <Card.Body>
              <ul className="list-unstyled mb-0">
                {(data.recommendations || []).map((r) => (
                  <li key={r.id} className="mb-2">
                    {r.done ? '✓' : '✗'} {r.label}
                  </li>
                ))}
              </ul>
              <Button className="mt-3" onClick={() => navigate(platformUrl('audit/access-reviews'))}>
                Open access reviews
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ConfirmModal
        isOpen={rotateAllOpen}
        onClose={() => setRotateAllOpen(false)}
        onConfirm={async () => {
          try {
            await rotateAllDueSecretsAPI();
            toast.success('Rotated all due secrets');
            setRotateAllOpen(false);
            load();
          } catch (e) {
            toast.error(e.message);
          }
        }}
        title="Rotate all due secrets?"
        message="This will mark overdue keys as rotated in the mock environment."
        confirmLabel="Rotate all"
      />
    </div>
  );
}
